import React, {useContext, useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../../_metronic/helpers'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'

import { ProductsContext } from '../../context'
import ProductSectionDetailModal from './ProductSectionDetail'
import ImportCollectionModal from '../../components/ImportCollectionModal'
import config from '../../../config.json'
import DataGridComponent from '../../components/DataGrid'

const ProductSectionsPage = () => {
  
  const { genericProducts, genericProductsLoaded, fetchProducts, productSections, productSectionsLoaded, fetchProductSections, createProductSection, updateProductSection, updateProductSectionsBatch, deleteProductSection } = useContext(ProductsContext)

  const [currentProductSection, setCurrentProductSection] = useState(null)
  const [loading, setLoading] = useState(false)
  const [importCollectionModalOpen, setImportCollectionModalOpen] = useState(false)

  const [updatedOrder, setUpdatedOrder] = useState(null)
  const [orderError, setOrderError] = useState(null)

  const sortedSections = useMemo(() => [...productSections].sort((a, b) => {
    const aOrder = Number.isFinite(a.order) ? a.order : Infinity
    const bOrder = Number.isFinite(b.order) ? b.order : Infinity
    return (aOrder - bOrder) || (a.title?.fr || '').localeCompare(b.title?.fr || '') || a.id.localeCompare(b.id)
  }), [productSections])
  
  const originalOrder = sortedSections.map(section => section.id)
  // Keep pending moves when a section is edited, and reconcile additions/deletions.
  const sectionById = new Map(productSections.map(section => [section.id, section]))
  const pendingIds = new Set(updatedOrder || [])
  const effectiveOrder = updatedOrder
    ? [...updatedOrder.filter(id => sectionById.has(id)), ...originalOrder.filter(id => !pendingIds.has(id))]
    : originalOrder
  const orderedSections = effectiveOrder.map((id, order) => ({...sectionById.get(id), order}))
  const orderChanged = originalOrder.join(',') !== effectiveOrder.join(',')

  const handleSaveOrder = async () => {
    setLoading(true)
    setOrderError(null)
    try {
      const updates = {}
      effectiveOrder.forEach((id, order) => {
        if(sectionById.get(id).order !== order) {
          updates[id] = {order}
        }
      })
      const response = await updateProductSectionsBatch(updates)
      if(response?.error) {
        throw response.error
      }
      setUpdatedOrder(null)
    } catch(error) {
      setOrderError('Could not save the updated order. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if(!productSectionsLoaded) {
      fetchProductSections()
    }
  }, [productSectionsLoaded, fetchProductSections])

  useEffect(() => {
    if(!genericProductsLoaded) {
      fetchProducts('generic')
    }
  }, [genericProductsLoaded, fetchProducts])

  const onCreateClick = () => {
    setCurrentProductSection({
      id: '',
      title: {},
      cover_image: ''
    })
  }

  const onEditClick = (productSection) => {
    setCurrentProductSection(productSection)
  }

  const onDetailSubmit = async (data) => {
    if(!data.id) {
      await createProductSection({
        ...data,
        location: data.location || 'france'
      })
    } else {
      await updateProductSection(data.id, {
        ...data,
        location: data.location || 'france'
      })
    }
    setCurrentProductSection(null)
  }

  const onDetailDelete = async () => {
    if(!confirm(`Are you sure you want to delete this product section? (${currentProductSection?.title?.fr || currentProductSection.id})`)) return
    await deleteProductSection(currentProductSection.id)
    setCurrentProductSection(null)
  }

  const itemWithId = (array, id) => {
    return array.find(i => i.id === id)
  }

  const promptImportCollection = () => {
    setImportCollectionModalOpen(true)
  }

  return (
    <>
      <ToolbarCustom
        pageTitle={'Product Sections'}
        primaryButton={productSectionsLoaded && !loading && { onClick: onCreateClick, title: 'Add' }}
        loading={!productSectionsLoaded || loading}
        filterElements={[(
          orderChanged && <button key='save-order' className='btn btn-sm fw-bold btn-primary' onClick={handleSaveOrder} disabled={loading}>
            <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-3' />
            Save updated order
          </button>
        ), (
          config.environment !== 'staging' && <button className='btn btn-sm fw-bold btn-primary' onClick={promptImportCollection} disabled={loading}>
            <KTSVG path='/media/icons/duotune/arrows/arr037.svg' className='svg-icon-3' />
            Import from staging
          </button>
        )]}
      />
      {orderError && <div className='alert alert-danger mt-3' role='alert'>{orderError}</div>}
      <div className='mt-3'>
        <DataGridComponent
          id='product-sections'
          rows={orderedSections}
          loading={!productSectionsLoaded || loading}
          reorderable={!loading}
          onOrderChanged={setUpdatedOrder}
          columns={[
            {
              field: 'cover_image',
              headerName: 'Image',
              width: 100,
              sortable: false,
              filterable: false,
              renderCell: ({value}) => value ? <img src={value} alt='' className='rounded' style={{width: 64, height: 40, objectFit: 'cover'}} /> : null
            },
            {
              field: 'title',
              headerName: 'Title',
              flex: 2,
              minWidth: 180,
              valueFormatter: title => title?.fr || 'Missing title',
              sortComparator: (a, b) => (a?.fr || '').localeCompare(b?.fr || '')
            },
            {
              field: 'location',
              headerName: 'Location',
              width: 120,
              valueFormatter: location => location === 'france' ? 'France' : location === 'europe' ? 'Europe' : location || '—'
            },
            {
              field: 'products',
              headerName: 'Products',
              flex: 3,
              minWidth: 240,
              valueGetter: products => (products || []).map(id => {
                const product = itemWithId(genericProducts, id)
                return product ? `${product.name?.fr || id}${!product.active ? ' (inactive)' : ''}` : id
              }).join(', ')
            },
            {
              field: 'actions',
              headerName: 'Actions',
              width: 100,
              sortable: false,
              filterable: false,
              renderCell: ({row}) => (
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary'
                  aria-label='Edit product section'
                  disabled={loading}
                  onClick={() => onEditClick(sectionById.get(row.id))}
                >
                  <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-2' />
                </button>
              )
            }
          ]}
        />
      </div>

      <ProductSectionDetailModal
        show={Boolean(currentProductSection)}
        handleClose={() => setCurrentProductSection(null)}
        section={currentProductSection}
        setSection={setCurrentProductSection}
        onSubmit={onDetailSubmit}
        onDelete={onDetailDelete}
        products={genericProducts} />
      <ImportCollectionModal
        isOpen={importCollectionModalOpen}
        onClose={() => setImportCollectionModalOpen(false)}
        collectionName={"product_sections"}
        sourceEnvironment={"staging"}
        onImportComplete={async () => {
          // reload collection
          await fetchProductSections()
        }} />
    </>
  )
}

const ProductSectionsWrapper = () => {
  return (
    <>
      <ProductSectionsPage />
    </>
  )
}

export default ProductSectionsWrapper
